import { Route, Routes, useLocation } from 'react-router-dom';
import { Suspense, lazy, useEffect, useState } from 'react';
import Header from './components/Header';
import Footer from './components/Footer';
import CartDrawer from './components/CartDrawer';
import BrandTicker from './components/BrandTicker';
import ScrollToTop from './components/ScrollToTop';
import MobileCartBar from './components/MobileCartBar';
import { Loader } from './components/Loader';
import { ToastProvider } from './context/ToastContext';

const HomePage = lazy(() => import('./pages/HomePage'));
const ShopPage = lazy(() => import('./pages/ShopPage'));
const BrandsPage = lazy(() => import('./pages/BrandsPage'));
const ProductPage = lazy(() => import('./pages/ProductPage'));
const ProductImagePage = lazy(() => import('./pages/ProductImagePage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const CheckoutSuccessPage = lazy(() => import('./pages/CheckoutSuccessPage'));
const CheckoutDevConfirmPage = lazy(() => import('./pages/CheckoutDevConfirmPage'));
const HowItWorksPage = lazy(() => import('./pages/HowItWorksPage'));
const AlertsPage = lazy(() => import('./pages/AlertsPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const FaqPage = lazy(() => import('./pages/FaqPage'));
const SellPage = lazy(() => import('./pages/SellPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));

function PageTransition({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [display, setDisplay] = useState(children);
  const [transitioning, setTransitioning] = useState(false);

  useEffect(() => {
    setTransitioning(true);
    const t = setTimeout(() => {
      setDisplay(children);
      setTransitioning(false);
    }, 200);
    return () => clearTimeout(t);
  }, [location.pathname]);

  return (
    <div className={`transition-opacity duration-200 ${transitioning ? 'opacity-40' : 'opacity-100'}`}>
      {display}
    </div>
  );
}

function AppContent() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  const isCheckout = location.pathname.startsWith('/checkout');

  return (
    <div className="flex min-h-screen flex-col bg-bone text-slate">
      <ScrollToTop />
      {!isAdmin && !isCheckout && <Header />}
      {!isAdmin && !isCheckout && <BrandTicker />}
      <main className={`flex-1 ${isAdmin || isCheckout ? '' : ''}`}>
        <Suspense fallback={<Loader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/shop/:category" element={<ShopPage />} />
            <Route path="/brands" element={<BrandsPage />} />
            <Route path="/product/:slug" element={<ProductPage />} />
            <Route path="/product/:slug/image/:index" element={<ProductImagePage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
            <Route path="/checkout/dev-confirm" element={<CheckoutDevConfirmPage />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/alerts" element={<AlertsPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/sell" element={<SellPage />} />
            <Route path="/admin/*" element={<AdminPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      {!isAdmin && !isCheckout && <Footer />}
      {!isAdmin && !isCheckout && <CartDrawer />}
      {!isAdmin && !isCheckout && <MobileCartBar />}
    </div>
  );
}

export default function App() {
  return (
    <ToastProvider>
      <AppContent />
    </ToastProvider>
  );
}
